import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowLeft, Info } from 'lucide-react';
import { useRef } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { QueryKey } from '@/Config/api/queryKey';
import {
  DiscordTemplateGuildDto,
  DiscordTemplateGuildResourcesDto,
  DiscordTemplateInstallInput,
  DiscordTemplateInstallResponse,
} from '@/Domain/api/Action/dto/DiscordTemplate.dto';
import { DiscordTemplate } from '@/Domain/components/Action/DiscordTemplates/discordTemplates.const';
import { Alert, AlertDescription } from '@/Domain/components/Shared/Alert/Alert';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/Domain/components/Shared/Dialog/Dialog';
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from '@/Domain/components/Shared/Field';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/Domain/components/Shared/Select/Select';
import { Skeleton } from '@/Domain/components/Shared/Skeleton/Skeleton';
import { DiscordIcon } from '@/Domain/components/icons/Discord.icon';

type Props = {
  template: DiscordTemplate;
  onBack: () => void;
  onClose: () => void;
};

const formSchema = z.object({
  guild_id: z.string().min(1),
  channel_id: z.string().min(1),
  role_id: z.string(),
});

type FormValues = z.infer<typeof formSchema>;

const GUILD_SELECT_ID = 'discord-template-guild-select';
const CHANNEL_SELECT_ID = 'discord-template-channel-select';
const ROLE_SELECT_ID = 'discord-template-role-select';
const ROLE_NONE_SENTINEL = '__none__';

export const DiscordTemplateInstallDialog = ({ template, onBack, onClose }: Props) => {
  const { t } = useTranslation('actions_page');
  const titleRef = useRef<HTMLHeadingElement>(null);
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { guild_id: '', channel_id: '', role_id: '' },
  });
  const guildId = useWatch({ control: form.control, name: 'guild_id' });

  const guildsQuery = useQuery<DiscordTemplateGuildDto[]>({
    queryKey: [QueryKey.actions.discordTemplates.guilds],
  });
  const resourcesQuery = useQuery<DiscordTemplateGuildResourcesDto>({
    queryKey: [QueryKey.actions.discordTemplates.resources, guildId],
    enabled: Boolean(guildId),
  });
  const installMutation = useMutation<
    DiscordTemplateInstallResponse,
    unknown,
    DiscordTemplateInstallInput
  >({
    mutationKey: [QueryKey.actions.discordTemplates.install],
  });
  const resourcesAreLoading = Boolean(guildId) && resourcesQuery.isLoading;
  const roles = resourcesQuery.data?.roles ?? [];
  const hasNoMentionableRoles = Boolean(resourcesQuery.data) && roles.length === 0;

  const submit = form.handleSubmit(async (values) => {
    const roleId = roles.some((role) => role.id === values.role_id) ? values.role_id : '';
    try {
      await installMutation.mutateAsync({
        template_key: template.installKey,
        guild_id: values.guild_id,
        channel_id: values.channel_id,
        ...(roleId ? { role_id: roleId } : {}),
      });
      onClose();
    } catch {
      return;
    }
  });

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent
        className="max-w-xl"
        data-testid="discord-template-install-dialog"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          titleRef.current?.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle ref={titleRef} tabIndex={-1} data-testid="discord-template-install-title">
            <DiscordIcon className="size-5" />
            {t('discord_templates.configure_title', { template: t(template.nameKey) })}
          </DialogTitle>
          <DialogDescription>{t('discord_templates.configure_description')}</DialogDescription>
        </DialogHeader>

        {guildsQuery.isError && (
          <Alert variant="destructive" data-testid="discord-template-guilds-error">
            <Info className="size-4" />
            <AlertDescription>{t('discord_templates.options_error')}</AlertDescription>
          </Alert>
        )}

        <form id="discord-template-install-form" onSubmit={submit}>
          <FieldGroup>
            <Controller
              control={form.control}
              name="guild_id"
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <FieldLabel htmlFor={GUILD_SELECT_ID}>
                    {t('discord_templates.guild_label')}
                  </FieldLabel>
                  <FieldDescription>{t('discord_templates.guild_description')}</FieldDescription>
                  {guildsQuery.isLoading ? (
                    <Skeleton
                      className="h-[34px] w-full rounded-xl"
                      data-testid="discord-template-guild-loading"
                    />
                  ) : (
                    <Select
                      value={field.value}
                      onValueChange={(value) => {
                        field.onChange(value);
                        form.setValue('channel_id', '');
                        form.setValue('role_id', '');
                      }}
                      disabled={installMutation.isPending}
                    >
                      <SelectTrigger
                        id={GUILD_SELECT_ID}
                        aria-invalid={fieldState.invalid}
                        data-testid={GUILD_SELECT_ID}
                      >
                        <SelectValue placeholder={t('discord_templates.guild_placeholder')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          {(guildsQuery.data ?? []).map((guild) => (
                            <SelectItem
                              key={guild.id}
                              value={guild.id}
                              data-testid={`discord-template-guild-option-${guild.id}`}
                            >
                              {guild.name}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  )}
                  <FieldError>
                    {fieldState.error && t('discord_templates.field_required')}
                  </FieldError>
                </Field>
              )}
            />

            <Controller
              control={form.control}
              name="channel_id"
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <FieldLabel htmlFor={CHANNEL_SELECT_ID}>
                    {t('discord_templates.channel_label')}
                  </FieldLabel>
                  <FieldDescription>{t('discord_templates.channel_description')}</FieldDescription>
                  {resourcesAreLoading ? (
                    <Skeleton
                      className="h-[34px] w-full rounded-xl"
                      data-testid="discord-template-channel-loading"
                    />
                  ) : (
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                      disabled={!guildId || resourcesQuery.isFetching || installMutation.isPending}
                    >
                      <SelectTrigger
                        id={CHANNEL_SELECT_ID}
                        aria-invalid={fieldState.invalid}
                        data-testid={CHANNEL_SELECT_ID}
                      >
                        <SelectValue placeholder={t('discord_templates.channel_placeholder')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          {(resourcesQuery.data?.channels ?? []).map((channel) => (
                            <SelectItem
                              key={channel.id}
                              value={channel.id}
                              data-testid={`discord-template-channel-option-${channel.id}`}
                            >
                              #{channel.name}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  )}
                  <FieldError>
                    {fieldState.error && t('discord_templates.field_required')}
                  </FieldError>
                </Field>
              )}
            />

            {!guildId ? null : resourcesQuery.isError ? (
              <Field className="min-h-28">
                <Alert variant="destructive" data-testid="discord-template-resources-error">
                  <Info className="size-4" />
                  <AlertDescription>{t('discord_templates.options_error')}</AlertDescription>
                </Alert>
              </Field>
            ) : hasNoMentionableRoles ? (
              <Field className="min-h-28">
                <Alert variant="info" data-testid="discord-template-no-roles">
                  <Info className="size-4" />
                  <AlertDescription>{t('discord_templates.no_roles')}</AlertDescription>
                </Alert>
              </Field>
            ) : (
              <Controller
                control={form.control}
                name="role_id"
                render={({ field, fieldState }) => (
                  <Field className="min-h-28" data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={ROLE_SELECT_ID}>
                      {t('discord_templates.role_label')}
                    </FieldLabel>
                    <FieldDescription>
                      {t('discord_templates.support_role_description')}
                    </FieldDescription>
                    {resourcesAreLoading ? (
                      <Skeleton
                        className="h-[34px] w-full rounded-xl"
                        data-testid="discord-template-role-loading"
                      />
                    ) : (
                      <Select
                        value={
                          roles.some((role) => role.id === field.value)
                            ? field.value
                            : ROLE_NONE_SENTINEL
                        }
                        onValueChange={(value) =>
                          field.onChange(value === ROLE_NONE_SENTINEL ? '' : value)
                        }
                        disabled={
                          !guildId || resourcesQuery.isFetching || installMutation.isPending
                        }
                      >
                        <SelectTrigger
                          id={ROLE_SELECT_ID}
                          aria-invalid={fieldState.invalid}
                          data-testid={ROLE_SELECT_ID}
                        >
                          <SelectValue placeholder={t('discord_templates.role_placeholder')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            <SelectItem
                              value={ROLE_NONE_SENTINEL}
                              data-testid="discord-template-role-option-none"
                            >
                              {t('discord_templates.role_placeholder')}
                            </SelectItem>
                            {roles.map((role) => (
                              <SelectItem
                                key={role.id}
                                value={role.id}
                                data-testid={`discord-template-role-option-${role.id}`}
                              >
                                @{role.name}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    )}
                  </Field>
                )}
              />
            )}
          </FieldGroup>
        </form>

        <DialogFooter className="!justify-between">
          <Button
            variant="secondary"
            onClick={onBack}
            disabled={installMutation.isPending}
            data-testid="discord-template-install-back"
          >
            <ArrowLeft data-icon="inline-start" />
            {t('discord_templates.back')}
          </Button>
          <Button
            type="submit"
            form="discord-template-install-form"
            loading={installMutation.isPending}
            data-testid="discord-template-install-submit"
          >
            {t('discord_templates.install_and_enable')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
